/**
 * MQTT Configuration & Brain Topic Constants
 *
 * MQTT 서버 설정 및 Brain 토픽 이름 정의
 * 문서 요구사항: 모든 토픽 이름을 외부 설정에서 변경 가능하도록 구현
 */
import { MQTT_PROTOCOL_CONFIG } from "./protocol_config";

// --- MQTT Connection ---

export interface MqttConfig {
  /** Legacy WebSocket URL alias */
  wsUrl: string;
  /** MQTT 브로커 WebSocket URL */
  brokerWsUrl: string;
  /** 개발용 기본 MQTT 브로커 인증 username */
  username: string;
  /** 개발용 기본 MQTT 브로커 인증 password */
  password: string;
  /** Legacy default vehicle ID */
  vehicleId: string;
  /** Legacy default controller ID */
  controllerId: string;
}

export const DEFAULT_MQTT_CONFIG: MqttConfig = {
  ...MQTT_PROTOCOL_CONFIG.defaults,
  username: "",
  password: "",
  vehicleId: "",
  controllerId: "",
};

// --- MQTT Topics (차량별 통신 방향) ---

export const mqttTopics = (vehicleId: string, userId: string) => ({
  /** 유저 → 차량 (Publish) */
  cockpitToVehicle: `${userId}/to/${vehicleId}`,
  /** 차량 → 유저 (Subscribe) */
  vehicleToCockpit: `${vehicleId}/to/${userId}`,
});

// --- Brain Topics (메시지 타입 구분) ---

/** 콕핏 → 차량: 제어 명령 */
export const BRAIN_TOPICS_COMMAND = {
  ...MQTT_PROTOCOL_CONFIG.brainTopics.command,
} as const;

/** 차량 UI → 차량: 차량 측 시스템 명령 */
export const BRAIN_TOPICS_VEHICLE_COMMAND = {
  ...MQTT_PROTOCOL_CONFIG.brainTopics.vehicleCommand,
} as const;

/** 콕핏 → 차량: 제어권 / 하트비트 */
export const BRAIN_TOPICS_CONTROL = {
  ...MQTT_PROTOCOL_CONFIG.brainTopics.control,
} as const;

/** 시스템 ack 및 진단성 토픽 */
export const BRAIN_TOPICS_SYSTEM = {
  ...MQTT_PROTOCOL_CONFIG.brainTopics.system,
} as const;

/** 콕핏 → 차량: 상태 토픽 */
export const BRAIN_TOPICS_REMOTE_STATUS = {
  ...MQTT_PROTOCOL_CONFIG.brainTopics.remoteStatus,
} as const;

/** 차량 → 콕핏: 상태 수신 */
export const BRAIN_TOPICS_STATUS = {
  ...MQTT_PROTOCOL_CONFIG.brainTopics.status,
} as const;

/** 차량 → 콕핏: 하트비트 */
export const BRAIN_TOPICS_HEARTBEAT = {
  ...MQTT_PROTOCOL_CONFIG.brainTopics.heartbeat,
} as const;

/**
 * 차량 → 콕핏: 카메라별 캘리브레이션 파라미터 (새 포맷, brain_msgs.proto.KeyValueArray)
 * 각 토픽은 단일 카메라의 extrinsic_homography, intrinsic 등을 포함
 */
export const BRAIN_TOPICS_CAMERA_SETTINGS = {
  ...MQTT_PROTOCOL_CONFIG.brainTopics.cameraSettings,
} as const;

export type CameraSettingsTopic =
  (typeof BRAIN_TOPICS_CAMERA_SETTINGS)[keyof typeof BRAIN_TOPICS_CAMERA_SETTINGS];

/** 모든 카메라 settings 토픽 목록 */
export const ALL_CAMERA_SETTINGS_TOPICS: CameraSettingsTopic[] = Object.values(
  BRAIN_TOPICS_CAMERA_SETTINGS,
);


export type BrainTopic =
  | (typeof BRAIN_TOPICS_COMMAND)[keyof typeof BRAIN_TOPICS_COMMAND]
  | (typeof BRAIN_TOPICS_VEHICLE_COMMAND)[keyof typeof BRAIN_TOPICS_VEHICLE_COMMAND]
  | (typeof BRAIN_TOPICS_CONTROL)[keyof typeof BRAIN_TOPICS_CONTROL]
  | (typeof BRAIN_TOPICS_SYSTEM)[keyof typeof BRAIN_TOPICS_SYSTEM]
  | (typeof BRAIN_TOPICS_REMOTE_STATUS)[keyof typeof BRAIN_TOPICS_REMOTE_STATUS]
  | (typeof BRAIN_TOPICS_STATUS)[keyof typeof BRAIN_TOPICS_STATUS]
  | (typeof BRAIN_TOPICS_HEARTBEAT)[keyof typeof BRAIN_TOPICS_HEARTBEAT]
  | (typeof BRAIN_TOPICS_CAMERA_SETTINGS)[keyof typeof BRAIN_TOPICS_CAMERA_SETTINGS];


/** Brain Topic → Proto type name 매핑 (signal_mapping.md 기준) */
export const BRAIN_TOPIC_TYPE_NAMES: Record<string, string> = {
  ...MQTT_PROTOCOL_CONFIG.brainTopicTypeNames,
};
